import React, { useState } from 'react';
import { Users, UserPlus, Shield, Eye, Search as SearchIcon, X, ChevronDown, Trash2, Crown } from 'lucide-react';
interface Member {
  id: string;
  name: string;
  email: string;
  initials: string;
  role: 'Admin' | 'Analyst' | 'Responder' | 'Viewer';
}
interface Team {
  id: number;
  name: string;
  description: string;
  members: Member[];
}
const roleColors: Record<string, string> = {
  Admin: 'bg-astro-green/10 text-astro-green border-astro-green/20',
  Analyst: 'bg-white/5 text-white border-white/10',
  Responder: 'bg-white/5 text-astro-text-secondary border-white/10',
  Viewer: 'bg-white/5 text-astro-text-secondary/70 border-white/5'
};
const roleIcons: Record<string, React.ReactNode> = {
  Admin: <Crown size={12} data-id="element-2421" />,
  Analyst: <Shield size={12} data-id="element-2422" />,
  Responder: <Users size={12} data-id="element-2423" />,
  Viewer: <Eye size={12} data-id="element-2424" />
};
const initialTeams: Team[] = [{
  id: 1,
  name: 'SOC Team Alpha',
  description: '24/7 Security Operations Center monitoring and response',
  members: [{
    id: 'm1',
    name: 'Sarah Chen',
    email: 'sarah.chen@astrolayb.com',
    initials: 'SC',
    role: 'Admin'
  }, {
    id: 'm2',
    name: 'James Wilson',
    email: 'j.wilson@astrolayb.com',
    initials: 'JW',
    role: 'Analyst'
  }, {
    id: 'm3',
    name: 'Priya Patel',
    email: 'p.patel@astrolayb.com',
    initials: 'PP',
    role: 'Analyst'
  }, {
    id: 'm4',
    name: 'Alex Rivera',
    email: 'a.rivera@astrolayb.com',
    initials: 'AR',
    role: 'Responder'
  }, {
    id: 'm5',
    name: 'Maya Kim',
    email: 'm.kim@astrolayb.com',
    initials: 'MK',
    role: 'Responder'
  }, {
    id: 'm6',
    name: 'David Lee',
    email: 'd.lee@astrolayb.com',
    initials: 'DL',
    role: 'Viewer'
  }]
}];
export function TeamsPage() {
  const [teams, setTeams] = useState<Team[]>(initialTeams);
  const [selectedTeamId, setSelectedTeamId] = useState<number>(teams[0]?.id ?? 0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDesc, setNewTeamDesc] = useState('');
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState<Member['role']>('Analyst');
  const [roleFilter, setRoleFilter] = useState<Member['role'] | 'All'>('All');
  const selectedTeam = teams.find(t => t.id === selectedTeamId);
  const filteredMembers = selectedTeam ? roleFilter === 'All' ? selectedTeam.members : selectedTeam.members.filter(m => m.role === roleFilter) : [];
  const handleCreateTeam = () => {
    if (!newTeamName.trim()) return;
    const newTeam: Team = {
      id: Date.now(),
      name: newTeamName.trim(),
      description: newTeamDesc.trim() || 'New security team',
      members: []
    };
    setTeams(prev => [...prev, newTeam]);
    setSelectedTeamId(newTeam.id);
    setNewTeamName('');
    setNewTeamDesc('');
    setShowCreateModal(false);
  };
  const handleRemoveMember = (memberId: string) => {
    setTeams(prev => prev.map(t => t.id === selectedTeamId ? {
      ...t,
      members: t.members.filter(m => m.id !== memberId)
    } : t));
  };
  const handleChangeRole = (memberId: string, newRole: Member['role']) => {
    setTeams(prev => prev.map(t => t.id === selectedTeamId ? {
      ...t,
      members: t.members.map(m => m.id === memberId ? {
        ...m,
        role: newRole
      } : m)
    } : t));
  };
  const handleAddMember = () => {
    if (!newMemberName.trim() || !newMemberEmail.trim()) return;
    const nameParts = newMemberName.trim().split(' ');
    const initials = nameParts.length >= 2 ? (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase() : newMemberName.trim().slice(0, 2).toUpperCase();
    const newMember: Member = {
      id: 'm-' + Date.now(),
      name: newMemberName.trim(),
      email: newMemberEmail.trim(),
      initials,
      role: newMemberRole
    };
    setTeams(prev => prev.map(t => t.id === selectedTeamId ? {
      ...t,
      members: [...t.members, newMember]
    } : t));
    setNewMemberName('');
    setNewMemberEmail('');
    setNewMemberRole('Analyst');
    setShowAddMemberModal(false);
  };
  return <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-2425">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8" data-id="element-2426">
        <div data-id="element-2427">
          <h1 className="text-3xl font-bold text-white mb-2" data-id="element-2428">Teams</h1>
          <p className="text-astro-text-secondary text-lg" data-id="element-2429">
            Manage your security teams and assign roles
          </p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2 px-4 py-2 bg-astro-green-dark hover:bg-astro-green text-white text-sm font-medium rounded transition-colors shadow-[0_0_15px_rgba(34,197,94,0.15)]" data-id="element-2430">

          <UserPlus size={16} data-id="element-2431" />
          Create Team
        </button>
      </div>

      {/* Team Selector Tabs */}
      {teams.length > 1 && <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2" data-id="element-2432">
          {teams.map(team => <button key={team.id} onClick={() => setSelectedTeamId(team.id)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${selectedTeamId === team.id ? 'bg-astro-green/15 text-astro-green border border-astro-green/30' : 'text-astro-text-secondary hover:text-white border border-transparent hover:border-astro-border'}`} data-id="element-2433">

              {team.name}
              <span className="ml-2 text-xs opacity-60" data-id="element-2434">
                {team.members.length}
              </span>
            </button>)}
        </div>}

      {selectedTeam && <>
          {/* Team Info Card */}
          <div className="bg-astro-card border border-astro-border rounded-lg p-6 mb-6" data-id="element-2435">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-id="element-2436">
              <div className="flex items-center gap-4" data-id="element-2437">
                <div className="w-14 h-14 rounded-lg bg-astro-green/10 flex items-center justify-center text-astro-green shrink-0" data-id="element-2438">
                  <Users size={28} data-id="element-2439" />
                </div>
                <div data-id="element-2440">
                  <h2 className="text-2xl font-bold text-white" data-id="element-2441">
                    {selectedTeam.name}
                  </h2>
                  <p className="text-sm text-astro-text-secondary mt-1" data-id="element-2442">
                    {selectedTeam.description}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm text-astro-text-secondary" data-id="element-2443">
                <div className="text-center" data-id="element-2444">
                  <div className="text-xl font-mono font-bold text-white" data-id="element-2445">
                    {selectedTeam.members.length}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider" data-id="element-2446">
                    Members
                  </div>
                </div>
                <div className="w-px h-10 bg-astro-border" data-id="element-2447"></div>
                <div className="text-center" data-id="element-2448">
                  <div className="text-xl font-mono font-bold text-astro-green" data-id="element-2449">
                    {selectedTeam.members.filter(m => m.role === 'Admin').length}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider" data-id="element-2450">
                    Admins
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Members List */}
          <div className="bg-astro-card border border-astro-border rounded-lg overflow-hidden" data-id="element-2451">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border-b border-astro-border gap-3" data-id="element-2452">
              <h3 className="font-bold text-white" data-id="element-2453">Team Members</h3>
              <div className="flex items-center gap-2 sm:gap-4 flex-wrap" data-id="element-2454">
                <div className="flex items-center gap-1 overflow-x-auto" data-id="element-2455">
                  <button onClick={() => setRoleFilter('All')} className={`px-2 py-1 rounded text-xs font-medium transition-all whitespace-nowrap ${roleFilter === 'All' ? 'bg-astro-green/15 text-astro-green border border-astro-green/30' : 'text-astro-text-secondary hover:text-white border border-transparent'}`} data-id="element-2456">

                    All
                    <span className="ml-1 opacity-60" data-id="element-2457">
                      {selectedTeam?.members.length}
                    </span>
                  </button>
                  {(['Admin', 'Analyst', 'Responder', 'Viewer'] as const).map(role => {
                const count = selectedTeam?.members.filter(m => m.role === role).length ?? 0;
                return <button key={role} onClick={() => setRoleFilter(roleFilter === role ? 'All' : role)} className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all whitespace-nowrap ${roleFilter === role ? 'bg-astro-green/15 text-astro-green border border-astro-green/30' : 'text-astro-text-secondary hover:text-white border border-transparent'}`} data-id="element-2458">

                          {roleIcons[role]}
                          <span className="hidden sm:inline" data-id="element-2459">{role}</span>
                          <span className="sm:hidden" data-id="element-2460">{role[0]}</span>
                          <span className="opacity-60" data-id="element-2461">{count}</span>
                        </button>;
              })}
                </div>
                <button onClick={() => setShowAddMemberModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-astro-green border border-astro-green/30 rounded hover:bg-astro-green/10 transition-colors whitespace-nowrap" data-id="element-2462">

                  <UserPlus size={14} data-id="element-2463" />
                  <span className="hidden sm:inline" data-id="element-2464">Add Member</span>
                  <span className="sm:hidden" data-id="element-2465">Add</span>
                </button>
              </div>
            </div>

            {filteredMembers.length === 0 && selectedTeam && selectedTeam.members.length > 0 ? <div className="p-12 text-center text-astro-text-secondary" data-id="element-2466">
                <Users size={32} className="mx-auto mb-3 opacity-40" data-id="element-2467" />
                <p className="text-sm" data-id="element-2468">
                  No{' '}
                  {roleFilter !== 'All' ? roleFilter.toLowerCase() + 's' : 'members'}{' '}
                  found.
                </p>
                <button onClick={() => setRoleFilter('All')} className="text-xs text-astro-green hover:text-astro-green-dark mt-2 transition-colors" data-id="element-2469">

                  Clear filter
                </button>
              </div> : filteredMembers.length === 0 ? <div className="p-12 text-center text-astro-text-secondary" data-id="element-2470">
                <Users size={32} className="mx-auto mb-3 opacity-40" data-id="element-2471" />
                <p className="text-sm" data-id="element-2472">
                  No members yet. Add team members to get started.
                </p>
              </div> : <div className="divide-y divide-astro-border" data-id="element-2473">
                {filteredMembers.map(member => <div key={member.id} className="flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors" data-id="element-2474">

                    <div className="flex items-center gap-3" data-id="element-2475">
                      <div className="w-9 h-9 rounded-full bg-astro-green-dim border border-astro-border flex items-center justify-center" data-id="element-2476">
                        <span className="text-xs font-bold text-astro-green" data-id="element-2477">
                          {member.initials}
                        </span>
                      </div>
                      <div data-id="element-2478">
                        <div className="text-sm font-medium text-white" data-id="element-2479">
                          {member.name}
                        </div>
                        <div className="text-xs text-astro-text-secondary" data-id="element-2480">
                          {member.email}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3" data-id="element-2481">
                      {/* Role Selector */}
                      <div className="relative" data-id="element-2482">
                        <select value={member.role} onChange={e => handleChangeRole(member.id, e.target.value as Member['role'])} className={`appearance-none pl-2 pr-7 py-1 rounded text-[11px] font-bold uppercase tracking-wider border cursor-pointer focus:outline-none ${roleColors[member.role]}`} data-id="element-2483">

                          <option value="Admin" data-id="element-2484">Admin</option>
                          <option value="Analyst" data-id="element-2485">Analyst</option>
                          <option value="Responder" data-id="element-2486">Responder</option>
                          <option value="Viewer" data-id="element-2487">Viewer</option>
                        </select>
                        <ChevronDown size={10} className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none opacity-50" data-id="element-2488" />

                      </div>

                      {/* Remove */}
                      <button onClick={() => handleRemoveMember(member.id)} className="p-1.5 text-astro-text-secondary/50 hover:text-astro-danger transition-colors" title="Remove member" data-id="element-2489">

                        <Trash2 size={14} data-id="element-2490" />
                      </button>
                    </div>
                  </div>)}
              </div>}
          </div>
        </>}

      {/* Create Team Modal */}
      {showCreateModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-id="element-2491">
          <div className="w-full max-w-[460px] bg-astro-card border border-astro-border rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-2492">
            <div className="flex items-center justify-between p-5 border-b border-astro-border" data-id="element-2493">
              <h2 className="text-lg font-bold text-white" data-id="element-2494">Create New Team</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-astro-text-secondary hover:text-white transition-colors p-1" data-id="element-2495">

                <X size={20} data-id="element-2496" />
              </button>
            </div>

            <div className="p-6 space-y-4" data-id="element-2497">
              <div data-id="element-2498">
                <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-2499">
                  Team Name
                </label>
                <input type="text" value={newTeamName} onChange={e => setNewTeamName(e.target.value)} placeholder="e.g. Incident Response" className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white placeholder:text-astro-text-secondary/50 focus:outline-none focus:border-astro-green/50 transition-colors" autoFocus data-id="element-2500" />

              </div>

              <div data-id="element-2501">
                <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-2502">
                  Description
                </label>
                <textarea value={newTeamDesc} onChange={e => setNewTeamDesc(e.target.value)} placeholder="What does this team do?" rows={3} className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white placeholder:text-astro-text-secondary/50 focus:outline-none focus:border-astro-green/50 transition-colors resize-none" data-id="element-2503" />

              </div>

              <div className="flex justify-end gap-3 pt-2" data-id="element-2504">
                <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm font-medium text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/30 transition-colors" data-id="element-2505">

                  Cancel
                </button>
                <button onClick={handleCreateTeam} disabled={!newTeamName.trim()} className="px-4 py-2 text-sm font-medium text-white bg-astro-green-dark hover:bg-astro-green rounded transition-colors shadow-[0_0_15px_rgba(34,197,94,0.15)] disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-2506">

                  Create Team
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Add Member Modal */}
      {showAddMemberModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-id="element-2507">
          <div className="w-full max-w-[460px] bg-astro-card border border-astro-border rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-2508">
            <div className="flex items-center justify-between p-5 border-b border-astro-border" data-id="element-2509">
              <h2 className="text-lg font-bold text-white" data-id="element-2510">Add Team Member</h2>
              <button onClick={() => setShowAddMemberModal(false)} className="text-astro-text-secondary hover:text-white transition-colors p-1" data-id="element-2511">

                <X size={20} data-id="element-2512" />
              </button>
            </div>

            <div className="p-6 space-y-4" data-id="element-2513">
              <div data-id="element-2514">
                <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-2515">
                  Full Name
                </label>
                <input type="text" value={newMemberName} onChange={e => setNewMemberName(e.target.value)} placeholder="e.g. Jordan Smith" className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white placeholder:text-astro-text-secondary/50 focus:outline-none focus:border-astro-green/50 transition-colors" autoFocus data-id="element-2516" />

              </div>

              <div data-id="element-2517">
                <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-2518">
                  Email Address
                </label>
                <input type="email" value={newMemberEmail} onChange={e => setNewMemberEmail(e.target.value)} placeholder="e.g. j.smith@astrolayb.com" className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white placeholder:text-astro-text-secondary/50 focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-2519" />

              </div>

              <div data-id="element-2520">
                <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-2521">
                  Role
                </label>
                <div className="grid grid-cols-4 gap-2" data-id="element-2522">
                  {(['Admin', 'Analyst', 'Responder', 'Viewer'] as const).map(role => <button key={role} onClick={() => setNewMemberRole(role)} className={`flex flex-col items-center gap-1 px-2 py-2.5 rounded border text-xs font-bold transition-all ${newMemberRole === role ? 'bg-astro-green/10 border-astro-green/30 text-astro-green' : 'bg-[#0a0e14] border-astro-border text-astro-text-secondary hover:text-white hover:border-white/20'}`} data-id="element-2523">

                        {roleIcons[role]}
                        {role}
                      </button>)}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2" data-id="element-2524">
                <button onClick={() => setShowAddMemberModal(false)} className="px-4 py-2 text-sm font-medium text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/30 transition-colors" data-id="element-2525">

                  Cancel
                </button>
                <button onClick={handleAddMember} disabled={!newMemberName.trim() || !newMemberEmail.trim()} className="px-4 py-2 text-sm font-medium text-white bg-astro-green-dark hover:bg-astro-green rounded transition-colors shadow-[0_0_15px_rgba(34,197,94,0.15)] disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-2526">

                  Add Member
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}